/**
 * ----------------------------------------------------------------------------------------------------------------------
 * PINNED VIDEO
 * ----------------------------------------------------------------------------------------------------------------------
 *
 * Drives the scroll-linked curtain wipe laid out in assets/pinned-video.css, and nothing else.
 *
 * Playback is deliberately left entirely to the theme — video-media's own inView autoplay plus the inline autoplay
 * in layout/theme.liquid. An earlier version of this file also started, paused and released each panel's <video> to
 * hold down the number of simultaneously decoding HD files, and that is exactly what stopped panels playing: a
 * released panel could reach the screen with its <source> still detached, or a panel that stopped on its own was
 * never restarted. The videos played fine before any of that existed, so none of it belongs here.
 *
 * Written per panel:
 *
 *   --pinned-video-clip      100% (hidden) -> 0% (fully revealed), the clip-path seam travelling bottom to top
 *   --pinned-video-parallax  the lift applied to the media while the panel is still being uncovered
 *
 * All instances share one rAF pass that reads every panel's geometry before any of them writes, so a scroll frame
 * costs a single reflow rather than one per panel.
 */

const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

const instances = new Set();

let frame = null;
let listening = false;

function schedule() {
  frame ??= requestAnimationFrame(render);
}

function render() {
  frame = null;

  for (const instance of instances) instance.measure();
  for (const instance of instances) instance.paint();
}

function startListening() {
  if (listening) return;
  listening = true;

  window.addEventListener('scroll', schedule, { passive: true });
  window.addEventListener('resize', schedule, { passive: true });
  window.visualViewport?.addEventListener('resize', schedule, { passive: true });
}

function clamp(value, min, max) {
  return Math.min(Math.max(value, min), max);
}

class PinnedVideo extends HTMLElement {
  #clip;

  connectedCallback() {
    this.section = this.closest('.shopify-section');

    // Reduced motion keeps the CSS geometry switched off, so the sections stay as plain stacked videos.
    if (!this.section || reducedMotion.matches) return;

    this.wipe = parseFloat(this.dataset.wipe) || 100;
    this.parallax = parseFloat(this.dataset.parallax) || 0;

    this.refresh();

    // connectedCallback fires as the parser reaches this element, so the sections after it do not exist yet and
    // every panel would believe it is the last one. Re-check once the document is complete.
    if (document.readyState === 'loading') {
      document.addEventListener(
        'DOMContentLoaded',
        () => {
          this.refresh();
          schedule();
        },
        { once: true }
      );
    }

    instances.add(this);
    startListening();
    schedule();
  }

  disconnectedCallback() {
    instances.delete(this);
  }

  /**
   * Works out where this panel sits in its run.
   *
   * The first panel has nothing behind it to be revealed over, so it is never clipped. The last panel has nothing
   * that will wipe over it, so it does not need the extra screen of pinned runway that only exists to hold a panel
   * still while its successor is revealed; `trailing` is mirrored onto the element for pinned-video.css.
   */
  refresh() {
    this.leading = !this.section.previousElementSibling?.querySelector('pinned-video');
    this.trailing = !this.section.nextElementSibling?.querySelector('pinned-video');

    this.toggleAttribute('leading', this.leading);
    this.toggleAttribute('trailing', this.trailing);
  }

  /**
   * Reads layout only. Runs for every instance before any of them writes.
   */
  measure() {
    // The panel is exactly 100svh tall, which calibrates the svh unit against the real viewport on every frame.
    this.unit = this.offsetHeight / 100;
    this.travelled = -this.section.getBoundingClientRect().top;
  }

  /**
   * Writes the seam position and the media lift.
   */
  paint() {
    const progress = this.leading ? 1 : clamp(this.travelled / (this.wipe * this.unit), 0, 1);
    const clip = (1 - progress) * 100;

    if (clip === this.#clip) return;

    this.#clip = clip;
    this.style.setProperty('--pinned-video-clip', `${clip}%`);
    this.style.setProperty('--pinned-video-parallax', `${(1 - progress) * this.parallax}px`);
  }
}

if (!window.customElements.get('pinned-video')) {
  window.customElements.define('pinned-video', PinnedVideo);
}

// Adding or reordering sections in the theme editor can change which panel leads or ends a run.
if (window.Shopify?.designMode) {
  for (const event of ['shopify:section:load', 'shopify:section:unload', 'shopify:section:reorder']) {
    document.addEventListener(event, () => {
      for (const instance of instances) instance.refresh();
      schedule();
    });
  }
}
